// генерирует XLSX-файл для одного заказа
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import axios from 'axios';
import { RootState } from '../store/store';
import type { AddressContainer, AppliedPromo, CartItem, Delivery, Product } from '../types/common';

type OrderForExport = RootState['common']['orders'][0];

let cachedCsrfToken: string | null = null;

async function getCsrfToken(): Promise<string> {
    if (cachedCsrfToken) return cachedCsrfToken;
    const { data } = await axios.get<{ csrfToken: string }>(`${process.env.REACT_APP_SERVER_URL}/csrf-token`, { withCredentials: true });
    cachedCsrfToken = data.csrfToken;
    return cachedCsrfToken;
}

type ProductLookupDTO = Pick<Product, 'articleNumber' | 'name' | 'price' | 'dimensions' | 'group'> & { id?: string; _id?: string };

// универсальный батч-лукап нужных полей товаров
async function fetchProductsDetails(ids: string[], fields?: (keyof Product)[]): Promise<Map<string, ProductLookupDTO>> {
    if (!ids.length) return new Map();
    const csrfToken = await getCsrfToken();
    const { data } = await axios.post<ProductLookupDTO[]>(
        `${process.env.REACT_APP_SERVER_URL}/products/lookup`,
        { ids, fields },
        { headers: { 'csrf-token': csrfToken }, withCredentials: true }
    );
    const map = new Map<string, ProductLookupDTO>();
    for (const d of data) {
        const key = (d as { _id?: string; id?: string })._id ?? (d as { _id?: string; id?: string }).id ?? '';
        if (key) map.set(key, d);
    }
    return map;
}

function applyThinBorders(ws: ExcelJS.Worksheet, fromRow: number, toRow: number, fromCol: number, toCol: number) {
    for (let r = fromRow; r <= toRow; r++) {
        for (let c = fromCol; c <= toCol; c++) {
            const cell = ws.getCell(r, c);
            cell.border = {
                top: { style: 'thin' },
                bottom: { style: 'thin' },
                left: { style: 'thin' },
                right: { style: 'thin' },
            };
        }
    }
}

function fmtNumber(n: number, digits = 2): string {
    return n.toFixed(digits);
}

function addMergedLine(
    ws: ExcelJS.Worksheet,
    text: string,
    align: 'left' | 'center' | 'right' = 'left',
    font: Partial<ExcelJS.Font> = { name: 'Calibri', size: 11, bold: false }
): number {
    const r = ws.addRow([text]);
    ws.mergeCells(r.number, 1, r.number, 10);
    const cell = ws.getCell(r.number, 1);
    cell.font = font;
    cell.alignment = { horizontal: align, vertical: 'middle', wrapText: true };
    return r.number;
}

// строгое форматирование адреса
function formatAddress(addr: AddressContainer | null | undefined): string {
    if (!addr) return '';
    if (typeof addr.raw === 'string') return addr.raw;
    const s = addr.struct;
    const parts: string[] = [s?.postalCode, s?.region, s?.city, s?.street, s?.house, s?.block, s?.flat]
        .map((v) => (v == null ? '' : String(v).trim()))
        .filter(Boolean);
    return parts.join(', ');
}

// // определение значения "Размер"
// function pickSizeFromGroup(product?: ProductLookupDTO): string {
//     const group = product?.group;
//     const url = group?.url?.trim() ?? '';
//     const val = group?.optionValue?.trim() ?? '';
//     if (url === 'Размер' && val) return val;
//     return '-';
// }

// определение значения "Размер/ Цвет"
function pickSizeOrColorFromGroup(product?: ProductLookupDTO): string {
    const group = product?.group;
    const url = group?.url?.trim() ?? '';
    const val = group?.optionValue?.trim() ?? '';
    if (url === 'Размер' && val) return val;
    if (url === 'Цвет' && val) return val;
    const anyProd: any = product ?? {};
    const colorFallback: string | undefined =
        typeof anyProd.productColor === 'string' && anyProd.productColor.trim()
            ? anyProd.productColor.trim()
            : typeof anyProd['productСolor'] === 'string' && anyProd['productСolor'].trim()
            ? anyProd['productСolor'].trim()
            : typeof anyProd.color === 'string' && anyProd.color.trim()
            ? anyProd.color.trim()
            : undefined;
    if (colorFallback) return colorFallback;
    return '-';
}

/* ======================= ПРОМОКОДЫ: только percentage ======================= */
type PromoDiscountType = 'percentage' | 'points';
interface PromoCodeRecord {
    code: string;
    discountType: PromoDiscountType;
    discountValue: number | string;
    status?: 'active' | 'inactive';
}
interface ResolvedPercentagePromo {
    code: string;
    percent: number;
}
function getOrderPromoCodeString(order: OrderForExport): string {
    const raw = (order as unknown as { promoCode?: string | AppliedPromo | null }).promoCode;
    if (!raw) return '';
    if (typeof raw === 'string') return raw.toUpperCase();
    const ap = raw as AppliedPromo;
    return (ap.code || '').toUpperCase();
}
async function resolvePercentagePromo(code: string): Promise<ResolvedPercentagePromo | null> {
    if (!code) return null;
    try {
        const { data } = await axios.get<PromoCodeRecord[]>(`${process.env.REACT_APP_SERVER_URL}/promocodes`, { withCredentials: true });
        const upper = code.toUpperCase();
        const found = (Array.isArray(data) ? data : []).find((p) => p.code?.toUpperCase?.() === upper);
        if (!found) return null;
        if (found.discountType !== 'percentage') return null;
        const dv = Number(found.discountValue);
        if (!Number.isFinite(dv) || dv <= 0) return null;
        return { code: upper, percent: dv };
    } catch {
        return null;
    }
}
/* ========================================================================== */

// Вспомогалка точного применения процента
function applyPercent(base: number, percent: number): number {
    return +(base * (1 - percent / 100)).toFixed(2);
}

export async function exportSingleOrderToExcel(order: OrderForExport): Promise<void> {
    const productIdsToLookup: string[] = [];
    for (const i of order.cart as CartItem[]) {
        productIdsToLookup.push(i.productId);
    }
    const fields: (keyof Product)[] = ['articleNumber', 'group', 'dimensions', 'name', 'price'];
    const productsMap = await fetchProductsDetails(productIdsToLookup, fields);

    const promoCodeRaw: string = getOrderPromoCodeString(order);
    const percentagePromo: ResolvedPercentagePromo | null = await resolvePercentagePromo(promoCodeRaw);
    const promoCodeForColumn: string = promoCodeRaw || '';
    const promoPercent: number | null = percentagePromo?.percent ?? null;
    const promoCodeForColumnDisplay: string = percentagePromo && promoCodeForColumn ? `${promoCodeForColumn} *` : promoCodeForColumn;

    type RowBase = {
        lookup?: ProductLookupDTO;
        item: CartItem;
        article: string;
        size: string;
        qty: number;
        unitPrice: number;
        effectiveItemPercent: number | null;
        baseUnit: number;
        specialApplied: boolean;
    };
    const rowsBase: RowBase[] = [];
    for (const item of order.cart as CartItem[]) {
        const lookup = productsMap.get(item.productId);
        const article = lookup?.articleNumber ?? '';
        const size = pickSizeOrColorFromGroup(lookup);
        const qty = item.quantity ?? 0;
        const unitPrice = typeof item.price === 'number' ? item.price : lookup?.price ?? 0;

        // ---------- приоритет спец-скидки над обычной ----------
        const specialDiscountedPrice =
            typeof (item as { specialDiscountedPrice?: number }).specialDiscountedPrice === 'number'
                ? (item as { specialDiscountedPrice: number }).specialDiscountedPrice
                : null;

        const specialPercentField =
            typeof (item as { specialDiscountPercent?: number }).specialDiscountPercent === 'number'
                ? (item as { specialDiscountPercent: number }).specialDiscountPercent
                : null;

        const derivedSpecialPercent = specialDiscountedPrice !== null && unitPrice > 0 ? +((1 - specialDiscountedPrice / unitPrice) * 100).toFixed(2) : null;

        const specialPercent = specialPercentField ?? derivedSpecialPercent;

        const discountedPrice =
            typeof (item as { discountedPrice?: number }).discountedPrice === 'number' ? (item as { discountedPrice: number }).discountedPrice : null;

        const discountPercentField = typeof item.discount === 'number' ? item.discount : null;
        const derivedDiscountPercent = discountedPrice !== null && unitPrice > 0 ? +((1 - discountedPrice / unitPrice) * 100).toFixed(2) : null;

        const normalPercent = discountPercentField ?? derivedDiscountPercent;

        let baseUnit = unitPrice;
        let effectiveItemPercent: number | null = null;
        let specialApplied = false;

        if (specialDiscountedPrice !== null) {
            baseUnit = +specialDiscountedPrice.toFixed(2);
            effectiveItemPercent = specialPercent ?? (unitPrice > 0 ? +((1 - baseUnit / unitPrice) * 100).toFixed(2) : null);
            specialApplied = true;
        } else if (specialPercent != null && specialPercent > 0) {
            baseUnit = applyPercent(unitPrice, specialPercent);
            effectiveItemPercent = specialPercent;
            specialApplied = true;
        } else if (discountedPrice !== null) {
            baseUnit = +discountedPrice.toFixed(2);
            effectiveItemPercent = normalPercent ?? (unitPrice > 0 ? +((1 - baseUnit / unitPrice) * 100).toFixed(2) : null);
        } else if (normalPercent != null && normalPercent > 0) {
            baseUnit = applyPercent(unitPrice, normalPercent);
            effectiveItemPercent = normalPercent;
        }

        rowsBase.push({ lookup, item, article, size, qty, unitPrice, effectiveItemPercent, baseUnit, specialApplied });
    }

    const hasAnySpecialDiscount: boolean = rowsBase.some((rb) => rb.specialApplied && (rb.effectiveItemPercent ?? 0) > 0);

    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Лист1');

    // --- ширина колонок ---
    ws.columns = [
        { key: 'A', width: 4.5 },
        { key: 'B', width: 15.0 },
        { key: 'C', width: 25.0 },
        { key: 'D', width: 14.0 },
        { key: 'E', width: 11.5 },
        { key: 'F', width: 14.0 },
        { key: 'G', width: 10.0 },
        { key: 'H', width: 15.0 },
        { key: 'I', width: 16.0 },
        { key: 'J', width: 16.0 },
    ];

    // 1) строка 1 - пустая
    ws.addRow([]);

    // 2) строка 2 - заголовок по центру A2:J2
    const titleRow = ws.addRow([`Заказ №${order.orderNumber}`]);
    ws.mergeCells(titleRow.number, 1, titleRow.number, 10);
    const titleCell = ws.getCell(titleRow.number, 1);
    titleCell.font = { name: 'Calibri', size: 14, bold: true };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };

    // строка 3 - пустая
    ws.addRow([]);

    // даты/реквизиты
    const dt = new Date(order.timestamp);
    const dd = String(dt.getDate()).padStart(2, '0');
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const yyyy = dt.getFullYear();
    const orderDateStr = `${dd}.${mm}.${yyyy}`;

    // 3) строка 4 - по левому краю
    addMergedLine(ws, `Дата заказа: ${orderDateStr}`, 'left');
    // строка 5 - пустая
    ws.addRow([]);

    // 4) строка 6 - по левому краю
    addMergedLine(ws, `Дата отгрузки покупателю: `, 'left');
    // строка 7 - пустая
    ws.addRow([]);

    // 5) строка 8 - по левому краю
    addMergedLine(ws, `Дата доставки покупателю: `, 'left');
    // строка 9 - пустая
    ws.addRow([]);

    // 6) строка 10 - по левому краю
    addMergedLine(ws, `Покупатель: ${order.user?.name ?? ''}`, 'left');

    // 7) строка 11 - Телефон
    addMergedLine(ws, `Телефон: ${order.user?.phone ?? ''}`, 'left');

    // 8) строка 12 - Адрес доставки
    const addressText = formatAddress(order.user?.address ?? null);
    addMergedLine(ws, `Адрес доставки: ${addressText}`, 'left');

    // строка 13 - пустая перед таблицей
    ws.addRow([]);

    // 9) шапка таблицы (14-я строка)
    const headerRow = ws.addRow([
        '№',
        'Артикул',
        'Наименование товара',
        'Размер/цвет',
        'Количество',
        'Цена за 1 шт, ₽',
        'Скидка, %',
        'Промокод',
        'Цена со скидкой за 1 шт, ₽',
        'Итоговая сумма',
    ]);
    headerRow.font = { name: 'Calibri', size: 11, bold: true };
    for (let c = 1; c <= 10; c++) {
        ws.getCell(headerRow.number, c).alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    }
    ws.getRow(headerRow.number).height = 28.5;

    let rowIndex = 1;
    let sumItemsFinal = 0; // сумма по позициям (без доставки)

    // 10) строки товаров
    for (const rb of rowsBase) {
        const { item, lookup, article, size, qty, unitPrice, effectiveItemPercent, baseUnit, specialApplied } = rb;

        // Скидка позиции в колонке G (special > discount) + пометка "спец. *"
        const discountDisplay: string | number | '' = effectiveItemPercent != null ? (specialApplied ? `${effectiveItemPercent} *` : effectiveItemPercent) : '';

        const effectivePromoPercent = promoPercent != null ? Math.max(0, Math.min(100, promoPercent)) : null;
        const discountedUnit: number = effectivePromoPercent != null ? applyPercent(baseUnit, effectivePromoPercent) : baseUnit;

        const totalPrice: number = +(discountedUnit * qty).toFixed(2);

        const r = ws.addRow([
            rowIndex, // A
            article, // B
            item.name ?? lookup?.name ?? '', // C
            size, // D
            qty, // E
            unitPrice, // F
            discountDisplay, // G
            promoCodeForColumnDisplay, // H
            discountedUnit, // I
            totalPrice, // J
        ]);
        const rn = r.number;

        ws.getCell(`F${rn}`).numFmt = '#,##0.00';
        ws.getCell(`I${rn}`).numFmt = '#,##0.00';
        ws.getCell(`J${rn}`).numFmt = '#,##0.00';

        (['E', 'F', 'G', 'H', 'I'] as const).forEach((col) => {
            ws.getCell(`${col}${rn}`).alignment = {
                horizontal: 'center',
                vertical: 'middle',
                wrapText: col === 'I',
            };
        });

        rowIndex++;
        if (!Number.isNaN(totalPrice)) {
            sumItemsFinal += totalPrice;
        }
    }

    let lastDataRow = ws.lastRow!.number;

    // 15) строка «Доставка»
    const delivery: Delivery | null | undefined = (order as unknown as { delivery?: Delivery | null }).delivery ?? null;
    const deliveryCost: number | null = delivery && typeof delivery.cost === 'number' ? Number(delivery.cost) : null;
    const deliveryMethod: string = delivery && typeof delivery.cost === 'number' ? String(delivery.method ?? '') : '';

    const deliveryRow = ws.addRow([
        rowIndex, // A
        deliveryMethod, // B
        'Доставка', // C
        '', // D
        '', // E
        '', // F
        '', // G
        '', // H
        '', // I
        deliveryCost != null ? deliveryCost : '-', // J
    ]);
    const dRowNo = deliveryRow.number;
    if (deliveryCost != null) {
        ws.getCell(`J${dRowNo}`).numFmt = '#,##0.00';
    }
    (['E', 'F', 'G', 'H', 'I'] as const).forEach((col) => {
        ws.getCell(`${col}${dRowNo}`).alignment = { horizontal: 'center', vertical: 'middle', wrapText: col === 'I' };
    });

    lastDataRow = dRowNo;

    // Рамки вокруг всей таблицы (A..J)
    applyThinBorders(ws, headerRow.number, lastDataRow, 1, 10);

    // 16) пустая строка после таблицы
    ws.addRow([]);

    // 17) «Итого с доставкой» - справа
    const totalWithDelivery = sumItemsFinal + (deliveryCost ?? 0);
    addMergedLine(ws, `Итого сумма заказа с доставкой: ${fmtNumber(totalWithDelivery)}`, 'right', {
        name: 'Calibri',
        size: 11,
        bold: true,
    });

    // 18) «Справочно: без доставки» - справа
    addMergedLine(ws, `Справочно: сумма Заказа без доставки: ${fmtNumber(sumItemsFinal)}`, 'right', {
        name: 'Calibri',
        size: 11,
        bold: false,
    });

    // 19) Соски после "СПРАВОЧНО"
    const needPromoFootnote: boolean = Boolean(percentagePromo && promoCodeForColumn);
    const needAnyFootnote: boolean = hasAnySpecialDiscount || needPromoFootnote;
    if (needAnyFootnote) {
        ws.addRow([]);
        ws.addRow([]);
        if (hasAnySpecialDiscount) {
            addMergedLine(ws, `* спец. скидка для сотрудника`, 'left');
        }
        if (needPromoFootnote) {
            addMergedLine(ws, `* скидка по промокоду ${percentagePromo!.percent}%`, 'left');
        }
    }

    // freeze до шапки таблицы (14-я строка)
    ws.views = [{ state: 'frozen', ySplit: headerRow.number }];

    // --- сохранение ---
    const fileName = `Заказ_${order.orderNumber}.xlsx`;
    const blob = new Blob([await wb.xlsx.writeBuffer()], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, fileName);
}
